import React from 'react';
import type { MutableRefObject } from 'react';
import type { InputRef } from 'antd';
import { Controller } from 'react-hook-form';
import type { Control, FieldError, UseFormSetValue } from 'react-hook-form';
import { AppButton, AppForm, AppFormLabel, AppSelect, AppTextField } from '@/components/atoms';
import { PostalCodeFields } from '@/components/molecules';
import type { TreatmentReportFormData } from '@/schemas/treatmentReportSchema';
import { normalizeTelephoneOrFaxInput } from '@/utils';
import type { TreatmentReportLabelChecks } from '../types';
import { TreatmentReportFieldRow } from './TreatmentReportFieldRow';

interface TreatmentReportMassagerFieldsProps {
  addressRef: MutableRefObject<InputRef | null>;
  clinicLocationError?: FieldError;
  control: Control<TreatmentReportFormData>;
  disabled: boolean;
  handleAddressSearch: () => void;
  handleClinicLocationChange: (value: number | null | undefined) => void;
  handleFillMassagerInfo: () => void;
  handleMassagerChange: (
    value: number | undefined,
    onChange: (value: number | undefined) => void
  ) => void;
  isAddressSearching: boolean;
  labelChecks: TreatmentReportLabelChecks;
  locationOptions: Array<{ value: number; label: string }>;
  massagerOptions: Array<{ value: number; label: string }>;
  onToggleLabel: (key: string) => void;
  setValue: UseFormSetValue<TreatmentReportFormData>;
  showClinicLocationError: boolean;
  watchedClinicLocationId?: number | null;
  watchedMassagerId?: number | null;
  watchedYm?: number | null;
}

export const TreatmentReportMassagerFields: React.FC<TreatmentReportMassagerFieldsProps> = ({
  addressRef,
  clinicLocationError,
  control,
  disabled,
  handleAddressSearch,
  handleClinicLocationChange,
  handleFillMassagerInfo,
  handleMassagerChange,
  isAddressSearching,
  labelChecks,
  locationOptions,
  massagerOptions,
  onToggleLabel,
  setValue,
  showClinicLocationError,
  watchedClinicLocationId,
  watchedMassagerId,
  watchedYm,
}) => (
  <>
    <TreatmentReportFieldRow
      labelKey="massager_id"
      label="施術者氏名"
      required
      checked={labelChecks.massager_id}
      onToggle={onToggleLabel}
    >
      <Controller
        name="massager_id"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
            <div className="mb-2 flex min-w-0 flex-wrap items-center gap-2">
              <AppSelect
                value={field.value}
                onChange={val => handleMassagerChange(val as number | undefined, field.onChange)}
                options={massagerOptions}
                placeholder="施術師選択"
                className="!w-[400px] min-w-0 shrink"
                disabled={disabled || !watchedYm}
                allowClear={false}
              />
              <AppSelect
                value={watchedClinicLocationId}
                onChange={val => handleClinicLocationChange(val as number | null | undefined)}
                options={locationOptions}
                placeholder="拠点選択"
                className="!w-auto min-w-[160px] max-w-full flex-[1_1_160px]"
                disabled={disabled || !watchedMassagerId}
                allowClear={false}
                status={showClinicLocationError ? 'error' : undefined}
              />
            </div>
            {showClinicLocationError && clinicLocationError?.message && (
              <div className="mb-2 text-red-600">{clinicLocationError.message}</div>
            )}
            <AppButton type="primary" onClick={handleFillMassagerInfo} disabled={disabled}>
              施術所情報引用
            </AppButton>
          </AppForm.Item>
        )}
      />
    </TreatmentReportFieldRow>

    <TreatmentReportFieldRow
      labelKey="location_name"
      label="施術所名"
      checked={labelChecks.location_name}
      onToggle={onToggleLabel}
    >
      <Controller
        name="location_name"
        control={control}
        render={({ field }) => (
          <AppTextField
            {...field}
            value={field.value ?? ''}
            maxLength={255}
            disabled={disabled}
            className="w-[400px] max-w-full"
          />
        )}
      />
    </TreatmentReportFieldRow>

    <div className="flex items-center gap-2">
      <AppFormLabel label="郵便番号" className="ml-[24.4px] flex-1" />
    </div>
    <div>
      <Controller
        name="postal_code1"
        control={control}
        render={({ field: f1, fieldState: { error: e1 } }) => (
          <Controller
            name="postal_code2"
            control={control}
            render={({ field: f2, fieldState: { error: e2 } }) => (
              <PostalCodeFields
                postalCode1={f1.value ?? ''}
                postalCode2={f2.value ?? ''}
                onPostalCode1Change={f1.onChange}
                onPostalCode2Change={f2.onChange}
                onPostalCode2Autofill={next =>
                  setValue('postal_code2', next, { shouldDirty: true, shouldValidate: true })
                }
                disabled={disabled}
                errorPostalCode1={e1?.message}
                errorPostalCode2={e2?.message}
                onSearch={handleAddressSearch}
                searchLoading={isAddressSearching}
              />
            )}
          />
        )}
      />
    </div>

    <TreatmentReportFieldRow
      labelKey="address"
      label="住所"
      checked={labelChecks.address}
      onToggle={onToggleLabel}
    >
      <Controller
        name="address"
        control={control}
        render={({ field }) => {
          const { ref: rhfRef, ...fieldProps } = field;
          return (
            <AppTextField
              {...fieldProps}
              ref={instance => {
                addressRef.current = instance;
                rhfRef(instance);
              }}
              value={field.value ?? ''}
              maxLength={255}
              disabled={disabled}
              className="w-[400px] max-w-full"
            />
          );
        }}
      />
    </TreatmentReportFieldRow>

    <TreatmentReportFieldRow
      labelKey="telephone"
      label="電話番号"
      checked={labelChecks.telephone}
      onToggle={onToggleLabel}
    >
      <Controller
        name="telephone"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
            <AppTextField
              {...field}
              value={field.value ?? ''}
              maxLength={13}
              disabled={disabled}
              className="w-[200px] max-w-full"
              placeholder="入力してください"
              onChange={e => {
                field.onChange(normalizeTelephoneOrFaxInput(e.target.value));
              }}
            />
          </AppForm.Item>
        )}
      />
    </TreatmentReportFieldRow>

    <TreatmentReportFieldRow
      labelKey="fax"
      label="FAX番号"
      checked={labelChecks.fax}
      onToggle={onToggleLabel}
    >
      <Controller
        name="fax"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
            <AppTextField
              {...field}
              value={field.value ?? ''}
              maxLength={13}
              disabled={disabled}
              className="w-[200px] max-w-full"
              placeholder="入力してください"
              onChange={e => {
                field.onChange(normalizeTelephoneOrFaxInput(e.target.value));
              }}
            />
          </AppForm.Item>
        )}
      />
    </TreatmentReportFieldRow>

    <TreatmentReportFieldRow
      labelKey="email"
      label="メールアドレス"
      checked={labelChecks.email}
      onToggle={onToggleLabel}
    >
      <Controller
        name="email"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
            <AppTextField
              {...field}
              value={field.value ?? ''}
              maxLength={255}
              disabled={disabled}
              className="w-[400px] max-w-full"
            />
          </AppForm.Item>
        )}
      />
    </TreatmentReportFieldRow>
  </>
);
